<template>
  <div>
    <content-container>
      <div class="digital-service">
        <transition name="fade" mode="out-in">
          <loader-with-message v-if="dataLoading" key="digital-service-loader" :has-error="dataFetchError"
                               @retry="fetchPackages"/>
          <div v-else key="digital-service-content">
            <transition name="fade" mode="out-in">
              <account-status-blocked v-if="userStatus === 'BLOCKED'" key="status-blocked"
                                      :resources-data="$props.resourcesData"/>
              <account-status-on-hold v-else-if="userStatus === 'ON_HOLD'" key="status-on-hold"
                                      :resources-data="$props.resourcesData"/>
              <div v-else key="status-active">
                <base-heading>{{ $t(`digitalServices.${serviceName}.pageTitle`) }}</base-heading>
                <h4 class="subtitle">{{ $t(`digitalServices.${serviceName}.title`) }}</h4>
                <div class="description">{{ $t(`digitalServices.${serviceName}.description`) }}</div>
                <div class="cards-container">
                  <digital-service-card v-for="(card, idx) in availablePackages"
                                        :class="getCardClasses(card)"
                                        :key="'package' + idx"
                                        :card-notice="card.action"
                                        :title="card.productName"
                                        :description="card.description"
                                        :logo="card.image ? card.image.url : ''"
                                        :price="card.actualPrice.formattedValue"
                                        :activation-disabled="card.hasOwnProperty('actionIsAble') ? !card.actionIsAble : false"
                                        :free-status="(card.freeTrial && !card.active) || card.actualPrice.value <= 0"
                                        :limited-free-label="card.freeTrial"
                                        :old-price="card.originalPrice ? card.originalPrice.formattedValue : ''"
                                        :service-active="card.active"
                                        :recurring-price="card.recurring"
                                        :service-name="serviceName"
                                        @open-tooltip="openDescriptionModal(card)"
                                        @activate-service="activateService(card)"
                                        @deactivate-service="deactivateService(card)"/>
                </div>
              </div>
            </transition>
          </div>
        </transition>
      </div>
    </content-container>
    <div class="digital-service">
      <!-- info modal -->
      <modal :visible="infoModalVisible"
             @modal-close="infoModalVisible = false"
             size="md"
             class="info-modal"
             :class="infoModalClass">
        <div class="info-modal-body">
          <div class="info-modal-body-header">
            <img v-if="cardData.image" :src="cardData.image.url" :alt="cardData.productName" class="logo"/>
            <h3>{{ cardData.productName }}</h3>
          </div>
          <p class="description" v-html="cardData.tooltipDescription"></p>
          <div v-if="cardData.actualPrice" class="actions">
            <div v-if="(cardData.freeTrial && !cardData.active) || cardData.actualPrice.value <= 0" class="free-period">
              <div class="free-period-title">
                <span v-if="cardData.freeTrial">{{ $t('digitalServices.freePeriodTitle') }}</span>
                <div class="free-title">{{ $t('digitalServices.freePeriodSubtitle') }}</div>
              </div>
              <div class="strike-price" v-if="false">
                <div class="inner">{{
                    cardData.originalPrice.formattedValue
                  }}&nbsp;<span>{{ $t('digitalServices.currency') }}/{{ $t('digitalServices.monthly') }}</span></div>
              </div>
            </div>
            <p v-else class="price">{{ cardData.actualPrice.formattedValue }}<span>{{
                $t('digitalServices.currency')
              }}/{{ $t('digitalServices.monthly') }}</span></p>
            <div class="buttons-container">
              <span v-if="alreadyActive" class="active-package-text">
                {{ $t('digitalServices.activePackage') }}
              </span>
              <base-button v-else-if="cardData.active && cardData.action !== 'ACTIVATE'" :disabled="hasActionIsAble"
                           variant="secondary" @click="deactivateService(cardData)">{{ $t('common.deactivate') }}
              </base-button>
              <base-button :disabled="actionDisabled || hasActionIsAble"
                           v-else-if="(!cardData.freeStatus) || cardData.actualPrice.value > 0" variant="primary"
                           @click="activateService(cardData)">{{ $t('common.activate') }}
              </base-button>
            </div>
          </div>
        </div>
      </modal>

      <!-- confirmation modal -->
      <confirmation-modal :visible="activationModal"
                          :closeButtonVisible="false"
                          @close="activationModal = false"
                          :cancel-button="true"
                          confirm-type="empty"
                          :dynamic-confirm-title="confirmationTitle"
                          :confirm-description="confirmationDescription"
                          :confirm-text="$t('common.activate')"
                          :loading="confirmationLoader"
                          @confirm="activateServiceApi"
                          :button-disabled="buttonDisabled"/>

      <!-- deactivation modal -->
      <confirmation-modal :visible="deactivationModal"
                          :closeButtonVisible="false"
                          @close="deactivationModal = false"
                          :cancel-button="true"
                          confirm-type="empty"
                          :dynamic-confirm-title="$t(`digitalServices.${serviceName}.confirmationModal.deactivateTitle`, {package: cardData ? cardData.productName : ''})"
                          :confirm-description="$t(`digitalServices.${serviceName}.confirmationModal.deactivateDescription`)"
                          :confirm-text="$t('common.deactivate')"
                          :loading="confirmationLoader"
                          @confirm="deactivateServiceApi"
                          :button-disabled="buttonDisabled"/>

      <confirmation-modal :visible="successActivation"
                          @close="resetData"
                          confirm-type="empty"
                          :confirm-title="$t(`digitalServices.${serviceName}.successModal.title`)"
                          :dynamic-data-description="$t(`digitalServices.${serviceName}.successModal.description`, {package: cardData ? cardData.productName : ''})"
                          :confirm-button="false"
                          :cancel-button="false">
        <div slot="extra-section">
          <div class="app-links">
            <a :href="$t(`digitalServices.${serviceName}.appLinks.appStore`)" target="_blank" rel="nofollow">
              <img class="store-button" src="/assets/images/app-store-badge.png" alt="app-store"/>
            </a>
            <a :href="$t(`digitalServices.${serviceName}.appLinks.googlePlay`)" target="_blank" rel="nofollow">
              <img class="store-button" src="/assets/images/google-play-badge.png" alt="google-play"/>
            </a>
          </div>
        </div>
      </confirmation-modal>
    </div>
    <digital-instruction-sticky-button :service-name="$props.serviceType"/>
  </div>
</template>
<script>
import ContentContainer from '@/components/common/main-layout/content-container'
import LoaderWithMessage from '@/components/common/loader-with-message'
import DigitalServiceCard from '@/components/digital-services/digital-service-card'
import ConfirmationModal from '@/components/common/modal/confirmation-modal'
import AccountStatusBlocked from '@/components/services/account-status/account-status-blocked'
import AccountStatusOnHold from '@/components/services/account-status/account-status-on-hold'
import DigitalInstructionStickyButton from '@/components/digital-services/digital-instruction-sticky-button'
import Modal from '@/components/common/modal/modal'
import errorMessageHandler from '@/mixins/errorMessageHandler'
import selectionBlockEvents from '@/mixins/selectionBlockEvents'

export default {
  name: 'digital-service-multiple',
  components: {
    ContentContainer,
    LoaderWithMessage,
    DigitalServiceCard,
    ConfirmationModal,
    AccountStatusBlocked,
    AccountStatusOnHold,
    DigitalInstructionStickyButton,
    Modal
  },
  props: {
    resourcesData: {
      type: Object,
      default: () => {
        return {}
      }
    },
    serviceType: {
      type: String,
      default: ''
    }
  },
  mixins: [errorMessageHandler, selectionBlockEvents],
  data () {
    return {
      dataLoading: true,
      dataFetchError: false,
      buttonDisabled: false,
      availablePackages: [],
      activatedPackages: [],
      userStatus: '',
      cardData: {},
      customerId: '',
      activationModal: false,
      deactivationModal: false,
      successActivation: false,
      infoModalVisible: false,
      confirmationLoader: false
    }
  },
  computed: {
    serviceName () {
      return this.$props.serviceType.toLowerCase()
    },
    actionDisabled () {
      return this.hasActionIsAble && !this.cardData.active && this.cardData.action === 'CHANGE'
    },
    confirmationTitle () {
      if (this.cardData.action === 'CHANGE') {
        return this.$t(`digitalServices.${this.serviceName}.confirmationModal.changeTitle`, { package: this.cardData.productName })
      } else {
        return this.$t(`digitalServices.${this.serviceName}.confirmationModal.title`, { package: this.cardData ? this.cardData.productName : '' })
      }
    },
    confirmationDescription () {
      if (this.cardData.action === 'CHANGE') {
        return this.$t(`digitalServices.${this.serviceName}.confirmationModal.changeDescription`)
      } else {
        return this.$t(`digitalServices.${this.serviceName}.confirmationModal.description`)
      }
    },
    alreadyActive () {
      return (!this.cardData.recurring && this.cardData.active) || (this.cardData.active && this.cardData.action === 'DEACTIVATE' && this.hasActionIsAble)
    },
    hasActionIsAble () {
      return Object.prototype.hasOwnProperty.call(this.cardData, 'actionIsAble') ? !this.cardData.actionIsAble : false
    },
    infoModalClass () {
      return {
        [`info-bg-${this.serviceName}`]: this.serviceName
      }
    }
  },
  methods: {
    fetchPackages () {
      if (this.$props.resourcesData.subscriptionInfo && this.$props.resourcesData.subscriptionInfo.customerId) {
        this.dataLoading = true
        this.dataFetchError = false
        this.blockSidebarSelection()
        const customerId = this.$props.resourcesData.subscriptionInfo.customerId

        this.$apiRequest
          .get(`user/${this.serviceName}?customerId=${customerId}`)
          .then((response) => {
            this.availablePackages = response.data.available || []
            this.activatedPackages = response.data.activated || []
            this.userStatus = response.data.contractStatus
            this.customerId = response.data.customerId
            this.dataLoading = false
          })
          .catch((e) => {
            this.dataFetchError = true
          })
          .finally(() => {
            this.unblockSidebarSelection()
          })
      }
    },
    activateServiceApi () {
      this.buttonDisabled = true
      this.confirmationLoader = true
      const customer = this.customerId ? this.customerId : this.$props.resourcesData.subscriptionInfo.customerId
      const dataToSend = {
        customerId: customer,
        externalId: this.cardData.externalId
      }
      const isChange = this.cardData.action === 'CHANGE' && this.cardData.actionIsAble
      const endPoint = isChange ? `user/${this.serviceName}/change` : `user/${this.serviceName}/activate`

      if (this.$props.resourcesData.subscriptionInfo && this.$props.resourcesData.subscriptionInfo.customerId) {
        this.$apiRequest[isChange ? 'put' : 'post'](endPoint, dataToSend)
          .then((response) => {
            if (response.status >= 200 && response.status < 400) {
              if (isChange) {
                this.activationModal = false
                this.fetchPackages()
                this.toast('success', {
                  title: this.$t('toastMessages.successToastTitle'),
                  message: this.$t(`digitalServices.${this.serviceName}.changeToast.successChangeMessage`)
                })
              } else {
                this.activationModal = false
                this.successActivation = true
              }
            }
          })
          .catch((e) => {
            this.handleErrorMessage(e.response.data)
          })
          .finally(() => {
            this.buttonDisabled = false
            this.confirmationLoader = false
          })
      }
    },
    deactivateServiceApi () {
      this.buttonDisabled = true
      this.confirmationLoader = true
      const dataToSend = {
        customerId: this.customerId
      }
      this.$apiRequest
        .post(`user/${this.serviceName}/deactivate`, dataToSend)
        .then((response) => {
          if (response.status >= 200 && response.status < 400) {
            this.deactivationModal = false
            this.fetchPackages()
            this.toast('success', {
              title: this.$t('toastMessages.successToastTitle'),
              message: this.$t(`digitalServices.${this.serviceName}.deactivationToast.successDeactivationMessage`)
            })
          }
        })
        .catch((e) => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.buttonDisabled = false
          this.confirmationLoader = false
        })
    },
    activateService (cardData) {
      this.infoModalVisible = false
      this.activationModal = true
      this.cardData = cardData
    },
    deactivateService (cardData) {
      this.infoModalVisible = false
      this.deactivationModal = true
      this.cardData = cardData
    },
    openDescriptionModal (cardData) {
      this.infoModalVisible = true
      this.cardData = cardData
    },
    resetData () {
      this.successActivation = false
      this.infoModalVisible = false
      this.fetchPackages()
    },
    getCardClasses (cardData) {
      return {
        // eslint-disable-next-line no-prototype-builtins
        'card-disabled': cardData.action === 'CHANGE' && cardData.hasOwnProperty('actionIsAble') ? !cardData.actionIsAble : false,
        'card-featured-green': cardData.action === 'DEACTIVATE' && cardData.active && this.serviceName === 'arena' // traženo je samo za Arenu da postoji zeleni featured border
      }
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.fetchPackages()
    })
  }
}
</script>
<style scoped lang="scss" src="./styles/digital-service-page.scss"></style>
